import useTypedTranslation from '@/hooks/useTypedTranslation';
import { HideComponentProps } from '@/types/props/components/common';
import { CommonEnum } from '@/types/translations/CommonEnum';
import { Fragment, useState } from 'react';
import DefaultButton from './default/DefaultButton';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';
import { twMerge } from '@/lib/twMerge';
import { Transition } from '@headlessui/react';
import clsx from 'clsx';

export default function HideComponent({
  hide = true,
  className,
  children,
  desktop = false,
  customAction,
  ...props
}: HideComponentProps) {
  const { translate: translateCommon } = useTypedTranslation<CommonEnum>('common');

  const [show, setShow] = useState(!hide);

  const baseClassName = clsx(
    'tw-transition-all',
    show ? 'tw-max-h-none' : 'tw-max-h-[250px] tw-overflow-hidden'
  );

  if (desktop) {
    return (
      <div {...props} className={twMerge([className, 'tw-relative'])}>
        <div className={baseClassName}>{children}</div>
        <Transition
          as={Fragment}
          show={!show}
          enter="tw-transition-all tw-duration-500 tw-ease-out"
          enterFrom="tw-opacity-0 tw-scale-95 tw-translate-y-8"
          enterTo="tw-opacity-100 tw-scale-100 tw-translate-y-0"
          leave="tw-transition-all tw-duration-500 tw-ease-in"
          leaveFrom="tw-opacity-100 tw-scale-100 tw-translate-y-0"
          leaveTo="tw-opacity-0 tw-cale-95 tw-translate-y-8"
        >
          <div className="tw-absolute tw-bottom-0 tw-left-0 tw-flex tw-h-1/3 tw-w-full tw-items-end tw-justify-center tw-bg-gradient-to-t tw-from-white/95 tw-via-gray-50 tw-to-transparent tw-transition-all tw-duration-300">
            <div className="tw-mb-12">
              <DefaultButton
                className="!tw-text-xs-custom tw-font-semibold tw-leading-[16.38px] tw-text-main tw-transition-all tw-duration-200 tw-ease-in-out hover:tw-bg-blue-100 hover:tw-text-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2 active:tw-scale-95 active:tw-text-blue-900 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                onClick={() => {
                  if (customAction) {
                    return customAction();
                  }
                  return setShow(prev => !prev);
                }}
              >
                {translateCommon(CommonEnum['see_more'])} <BiChevronDown />
              </DefaultButton>
            </div>
          </div>
        </Transition>

        <Transition
          as={Fragment}
          show={show}
          enter="tw-transition-all tw-duration-500 tw-ease-out"
          enterFrom="tw-opacity-0 tw-scale-95 tw-ranslate-y-8"
          enterTo="tw-opacity-100 tw-scale-100 tw-translate-y-0"
          leave="tw-transition-all tw-duration-500 tw-ease-in"
          leaveFrom="tw-opacity-100 tw-scale-100 tw-translate-y-0"
          leaveTo="tw-opacity-0 tw-scale-95 tw-translate-y-8"
        >
          <div className="tw-mt-4 tw-text-center">
            <DefaultButton
              className="!tw-text-xs-custom tw-font-semibold tw-leading-[16.38px] tw-text-main tw-transition-all tw-duration-200 tw-ease-in-out hover:tw-bg-blue-100 hover:tw-text-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2 active:tw-scale-95 active:tw-text-blue-900 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
              onClick={() => setShow(prev => !prev)}
            >
              {translateCommon(CommonEnum['see_less'])} <BiChevronUp />
            </DefaultButton>
          </div>
        </Transition>
      </div>
    );
  }

  return (
    <div className={clsx('tw-overflow-hidden', show ? 'tw-max-h-none' : 'tw-max-h-[250px]')}>
      <div {...props} className={twMerge([className, 'tw-relative'])}>
        <div className={baseClassName}>{children}</div>
        <Transition
          as={Fragment}
          show={!show}
          enter="tw-transition-all tw-duration-500 tw-ease-out"
          enterFrom="tw-opacity-0 tw-scale-95 tw-translate-y-8"
          enterTo="tw-opacity-100 tw-scale-100 tw-translate-y-0"
          leave="tw-transition-all tw-duration-500 tw-ease-in"
          leaveFrom="tw-opacity-100 tw-scale-100 tw-translate-y-0"
          leaveTo="tw-opacity-0 tw-cale-95 tw-translate-y-8"
        >
          <div className="tw-absolute tw-bottom-0 tw-left-0 tw-flex tw-h-[60%] tw-w-full tw-items-end tw-justify-center tw-bg-gradient-to-t tw-from-white/100 tw-via-gray-50 tw-to-transparent tw-transition-all tw-duration-300">
            <DefaultButton
              className="tw-mb-12 !tw-text-xs-custom tw-font-semibold tw-text-main tw-transition-all tw-duration-200 tw-ease-in-out hover:tw-bg-blue-100 hover:tw-text-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2 active:tw-scale-95 active:tw-text-blue-900 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
              onClick={() => setShow(prev => !prev)}
            >
              {translateCommon(CommonEnum['see_more'])} <BiChevronDown />
            </DefaultButton>
          </div>
        </Transition>

        <Transition
          as={Fragment}
          show={show}
          enter="tw-transition-all tw-duration-500 tw-ease-out"
          enterFrom="tw-opacity-0 tw-scale-95 tw-ranslate-y-8"
          enterTo="tw-opacity-100 tw-scale-100 tw-translate-y-0"
          leave="tw-transition-all tw-duration-500 tw-ease-in"
          leaveFrom="tw-opacity-100 tw-scale-100 tw-translate-y-0"
          leaveTo="tw-opacity-0 tw-scale-95 tw-translate-y-8"
        >
          <div className="tw-mt-4 tw-text-center">
            <DefaultButton
              className="tw-pb-[25px] !tw-text-xs-custom tw-font-semibold tw-leading-[16.38px] tw-text-main tw-transition-all tw-duration-200 tw-ease-in-out hover:tw-bg-blue-100 hover:tw-text-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2 active:tw-scale-95 active:tw-text-blue-900 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
              onClick={() => setShow(prev => !prev)}
            >
              {translateCommon(CommonEnum['see_less'])} <BiChevronUp />
            </DefaultButton>
          </div>
        </Transition>
      </div>
    </div>
  );
}
